import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import {
  SyncAbortedError,
  SyncWaitTimeoutError,
  summarizeSyncJob,
  waitForSyncJob,
  type HubspotSyncJobState,
} from '@/lib/hubspot-sync';
import { opportunitiesService } from '../services/opportunities.service';
import type { OpportunitiesSyncStatus } from '../types/opportunity.types';

export function fmtRelativeSync(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'hace unos segundos';
  if (mins === 1) return 'hace 1 min';
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs === 1) return 'hace 1 hora';
  if (hrs < 24) return `hace ${hrs} horas`;
  const days = Math.floor(hrs / 24);
  return `hace ${days} día${days === 1 ? '' : 's'}`;
}

export function lastSyncLabel(s: OpportunitiesSyncStatus): string {
  const base = s.lastSuccessAt
    ? `Última sincronización: ${fmtRelativeSync(s.lastSuccessAt)}`
    : 'Todavía no se sincronizó';
  const failedAfter =
    s.lastAttempt?.status === 'FAILED' &&
    (!s.lastSuccessAt || new Date(s.lastAttempt.at) > new Date(s.lastSuccessAt));
  if (failedAfter) return `${base} · el último intento falló`;
  // El backend cuenta una sincronización parcial como buena: se avisa igual.
  const partial = s.lastAttempt?.status === 'PARTIAL';
  return partial ? `${base} · algunos registros no se pudieron actualizar` : base;
}

interface Props {
  /** Se llama una vez cuando la actualización terminó: la página recarga la lista. */
  onSynced: () => void;
  pollIntervalMs?: number;
}

const FALLBACK = 'Probá de nuevo en unos minutos.';

export function OpportunitiesSyncControl({ onSynced, pollIntervalMs = 2_000 }: Props) {
  const { toast } = useToast();
  const [status, setStatus] = useState<OpportunitiesSyncStatus | null>(null);
  const [jobState, setJobState] = useState<HubspotSyncJobState | null>(null);
  const busy = useRef(false);
  // Se aborta al desmontar la página: corta la espera y evita avisos/recargas de una pantalla ya cerrada.
  const abort = useRef<AbortController | null>(null);
  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;

  const loadStatus = useCallback(async () => {
    try {
      const s = await opportunitiesService.syncStatus();
      setStatus(s);
      return s;
    } catch {
      return null;
    }
  }, []);

  const follow = useCallback(
    async (jobId: string) => {
      if (busy.current) return;
      busy.current = true;
      const controller = abort.current ?? new AbortController();
      abort.current = controller;
      setJobState('queued');
      try {
        const final = await waitForSyncJob(() => opportunitiesService.syncJob(jobId), {
          intervalMs: pollIntervalMs,
          onUpdate: (s) => setJobState(s.state),
          signal: controller.signal,
        });
        toast(summarizeSyncJob(final));
        onSyncedRef.current();
      } catch (err) {
        if (err instanceof SyncAbortedError || controller.signal.aborted) return;
        toast({
          title: 'No se pudo actualizar',
          description:
            err instanceof SyncWaitTimeoutError ? err.message : mensajeDeError(err, FALLBACK),
          variant: 'destructive',
        });
      } finally {
        busy.current = false;
        if (!controller.signal.aborted) {
          setJobState(null);
          await loadStatus();
        }
      }
    },
    [loadStatus, pollIntervalMs, toast]
  );

  useEffect(() => {
    const controller = new AbortController();
    abort.current = controller;
    void loadStatus().then((s) => {
      if (s?.runningJobId && !controller.signal.aborted) void follow(s.runningJobId);
    });
    return () => controller.abort();
  }, [loadStatus, follow]);

  const start = async () => {
    if (busy.current) return;
    try {
      const { jobId } = await opportunitiesService.startSync();
      await follow(jobId);
    } catch (err) {
      toast({
        title: 'No se pudo actualizar',
        description: mensajeDeError(err, FALLBACK),
        variant: 'destructive',
      });
    }
  };

  if (!status?.available) return null;
  const running = jobState !== null;

  return (
    <div className="flex items-center justify-end gap-2">
      <p className="text-xs text-muted-foreground">{lastSyncLabel(status)}</p>
      <Button size="sm" variant="outline" onClick={start} disabled={running}>
        <RefreshCw className={`h-4 w-4 mr-1 ${running ? 'animate-spin' : ''}`} />
        {jobState === 'queued' ? 'En cola…' : running ? 'Actualizando…' : 'Actualizar ahora'}
      </Button>
    </div>
  );
}
